import { calculateWorkOrderCosts } from '@calculations/cost/work-order';
import { CalculationConfig } from '@calculations/types/calculation-config';
import { storeToCalculationOperations } from '@ui/features/quote-builder/state/transforms/store-to-calculation-operations';
import { QuoteLineType } from '@ui/features/quote-builder/types/state/quote-line';
import { QuoteBuilderWorkOrderType } from '@ui/features/quote-builder/types/state/work-order';

type GenerateCostParams = Pick<
  QuoteLineType,
  'quotePriceUuids' | 'quotePrices'
> & {
  snapWorkOrder: QuoteBuilderWorkOrderType;
};

export type Category =
  | 'materialCost'
  | 'laborCost'
  | 'burdenCost'
  | 'serviceCost';

type SequenceData = { sequenceNumber: string; categories: Category[] };

type Sequences = Record<string, SequenceData>;

type QuotePriceCostData = {
  quotePriceUuid: string;
  quantity: string;
  unitCost: string;
  totalCost: string;
  sequences: Record<string, Record<Category, string>>;
};

type QuotePriceCosts = Record<string, QuotePriceCostData>;

export type CostBreakData = {
  sequences: Sequences;
  quotePriceCosts: QuotePriceCosts;
};

export function generateCostBreaks(
  params: GenerateCostParams,
  { decimalPlaces }: CalculationConfig,
): CostBreakData {
  const { quotePriceUuids, quotePrices, snapWorkOrder } = params;

  const sequences: Sequences = {};
  const quotePriceCosts: QuotePriceCosts = {};

  for (const uuid of quotePriceUuids) {
    const quantity = quotePrices[uuid].quantity;

    const sequenceCosts: Record<
      string,
      {
        materialCost: string;
        laborCost: string;
        burdenCost: string;
        serviceCost: string;
      }
    > = {};

    const { totalCost, unitCost, operationResults } = calculateWorkOrderCosts(
      {
        quantity,
        snapOperations: storeToCalculationOperations(snapWorkOrder),
      },
      { decimalPlaces },
    );

    for (const operation of operationResults) {
      const sequenceNumber = operation.sequenceNumber.toString();

      // Initialize sequence if it doesn't exist
      let sequence = sequences[sequenceNumber] ?? {
        sequenceNumber,
        categories: [],
      };

      const newCategories = new Set(sequence.categories);

      if (operation.materialCost.gt(0)) {
        newCategories.add('materialCost');
      }

      if (operation.laborCost.gt(0)) {
        newCategories.add('laborCost');
      }

      if (operation.burdenCost.gt(0)) {
        newCategories.add('burdenCost');
      }

      if (operation.serviceCost.gt(0)) {
        newCategories.add('serviceCost');
      }

      // Update sequence with merged categories
      sequence = {
        sequenceNumber,
        categories: Array.from(newCategories),
      };

      sequences[sequenceNumber] = sequence;

      sequenceCosts[sequenceNumber] = {
        materialCost: operation.materialCost.toFixed(decimalPlaces),
        laborCost: operation.laborCost.toFixed(decimalPlaces),
        burdenCost: operation.burdenCost.toFixed(decimalPlaces),
        serviceCost: operation.serviceCost.toFixed(decimalPlaces),
      };
    }

    quotePriceCosts[uuid] = {
      quotePriceUuid: uuid,
      quantity: quantity.toString(),
      unitCost: unitCost.toFixed(decimalPlaces),
      totalCost: totalCost.toFixed(decimalPlaces),
      sequences: sequenceCosts,
    };
  }

  return { sequences, quotePriceCosts };
}
